"use client";

import Image from "next/image";
import { storyblokEditable } from "@storyblok/react/rsc";
import { QuoteIcon } from "@/components/icons/QuoteIcon";
import { RichTextField } from "@/components/typography/RichTextField";

type PraxisTestimonialCardStoryblok = {
  text?: string;
  title?: string;
  subtitle?: string;
  website?: string | { url?: string; cached_url?: string; linktype?: string };
  logo?: { filename?: string; alt?: string };
  _uid: string;
  component: "praxis_testimonial_card";
  [k: string]: any;
};

type PraxisTestimonialCardProps = {
  blok: PraxisTestimonialCardStoryblok;
  fullWidth?: boolean;
};

const PraxisTestimonialCard = ({
  blok,
  fullWidth,
}: PraxisTestimonialCardProps) => {
  const websiteUrl = typeof blok.website === "string"
    ? blok.website
    : blok.website?.url || blok.website?.cached_url || "";
  const href = websiteUrl
    ? websiteUrl.startsWith("http")
      ? websiteUrl
      : `https://${websiteUrl}`
    : undefined;

  return (
    <div
      className={`flex flex-col justify-between bg-white rounded-2xl px-5 py-6 h-full ${fullWidth ? "w-full" : "w-[85vw] sm:w-[380px] md:w-[500px]"}`}
      {...storyblokEditable(blok)}
    >
      <div className="flex flex-col md:flex-row gap-3 items-start">
        <QuoteIcon className="shrink-0 w-6 h-6" />
        <RichTextField
          value={blok.text}
          className="text-copy text-black leading-[1.2] min-w-0"
        />
      </div>

      <div className="flex flex-col gap-3 mt-6">
        {blok.logo?.filename && (
          href ? (
            <a href={href} target="_blank" rel="noopener noreferrer">
              <Image
                src={blok.logo.filename}
                alt={blok.logo.alt || blok.title || ""}
                width={120}
                height={40}
                className="h-10 w-auto object-contain"
              />
            </a>
          ) : (
            <Image
              src={blok.logo.filename}
              alt={blok.logo.alt || blok.title || ""}
              width={120}
              height={40}
              className="h-10 w-auto object-contain"
            />
          )
        )}
        <div className="flex flex-col gap-0.5 min-w-0">
          {blok.title && (
            <p className="text-base md:text-lg font-bold text-black leading-tight">
              {blok.title}
            </p>
          )}
          {blok.subtitle && (
            <p className="text-sm text-gray-100">{blok.subtitle}</p>
          )}
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-sky-600 hover:underline truncate"
            >
              {websiteUrl.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default PraxisTestimonialCard;
